import { logger } from "@/lib/logger";
import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import SignatureCanvas from 'react-signature-canvas';

export interface SignatureData {
  signature_image: string;
  signature_url?: string;
  signed_by: string;
  signed_at: string;
  ip_address: string;
  user_agent: string;
  attestation: string;
}

interface ESignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSign: (signatureData: SignatureData) => Promise<void>;
  documentName: string;
  signingPurpose: string;
  siteId: string;
}

export function ESignatureModal({ 
  isOpen, 
  onClose, 
  onSign, 
  documentName, 
  signingPurpose,
  siteId 
}: ESignatureModalProps) {
  const sigPad = useRef<SignatureCanvas>(null);
  const [attestation, setAttestation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchIPAddress = async (): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('get-client-ip');
      if (error) throw error;
      return data?.ip || 'unknown';
    } catch (error) {
      logger.error('Failed to fetch IP address:', error);
      return 'unknown';
    }
  };

  const handleSign = async () => {
    if (!sigPad.current || sigPad.current.isEmpty()) {
      toast({
        title: "Signature required",
        description: "Please sign before submitting",
        variant: "destructive"
      });
      return;
    }

    if (!attestation.trim()) {
      toast({
        title: "Attestation required",
        description: "Please type your full name to attest",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const signatureImage = sigPad.current.toDataURL('image/png');
      const ipAddress = await fetchIPAddress();

      // Upload signature image to Supabase Storage
      const timestamp = Date.now();
      const fileName = `${siteId}/${user.id}_${timestamp}.png`;
      
      // Convert base64 to blob
      const base64Data = signatureImage.split(',')[1];
      const blob = await fetch(`data:image/png;base64,${base64Data}`).then(r => r.blob());
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('regulatory-signatures')
        .upload(fileName, blob, {
          contentType: 'image/png',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const signatureData: SignatureData = {
        signature_image: signatureImage,
        signature_url: uploadData?.path,
        signed_by: user.id,
        signed_at: new Date().toISOString(),
        ip_address: ipAddress,
        user_agent: navigator.userAgent,
        attestation: attestation.trim()
      };

      await onSign(signatureData);
      
      toast({
        title: "Document signed",
        description: "Your signature has been recorded"
      });
      
      onClose();
    } catch (error) {
      logger.error('Signature error:', error);
      toast({
        title: "Signature failed",
        description: error instanceof Error ? error.message : "Failed to save signature",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    sigPad.current?.clear();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Electronic Signature Required</DialogTitle>
          <DialogDescription>
            Document: <span className="font-semibold">{documentName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>By signing, you attest:</strong> {signingPurpose}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Sign below</Label>
            <div className="border-2 border-border rounded-lg bg-background overflow-hidden">
              <SignatureCanvas
                ref={sigPad}
                canvasProps={{
                  className: 'w-full h-40 cursor-crosshair',
                  style: { width: '100%', height: '160px' }
                }}
                backgroundColor="rgb(255, 255, 255)"
                penColor="rgb(0, 0, 0)"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleClear}
              type="button"
            >
              Clear Signature
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attestation">Type your full name to attest *</Label>
            <Input
              id="attestation"
              value={attestation}
              onChange={(e) => setAttestation(e.target.value)}
              placeholder="John Doe"
              disabled={isSubmitting}
            />
          </div>

          <Alert variant="default" className="bg-muted/50">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Your signature will be timestamped and include your IP address and user agent 
              for audit trail and regulatory compliance purposes (21 CFR Part 11).
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSign} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing...
              </>
            ) : (
              'Sign Document'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}